import type { ImageSearchHit, ImageSearchResult } from './types'

/** Flattens SWR pages into one list, dropping hits repeated across offset pages. */
export function flattenHits(pages: ImageSearchResult[] | undefined): ImageSearchHit[] {
  const seen = new Set<string>()
  const hits: ImageSearchHit[] = []

  for (const page of pages ?? []) {
    for (const hit of page?.hits ?? []) {
      if (seen.has(hit.id)) {
        continue
      }

      seen.add(hit.id)
      hits.push(hit)
    }
  }

  return hits
}

/**
 * True when no more pages should be fetched. Also true before any data exists,
 * so a failed first load isn't retried in a loop.
 */
export function isReachingEnd(
  pages: ImageSearchResult[] | undefined,
  pageSize: number
): boolean {
  return pages?.[0]?.hits.length === 0
    || (pages?.[pages.length - 1]?.hits.length ?? 0) < pageSize
}

/** Returns the hit `step` places away, e.g. a whole row with `step` set to the column count */
export function getAdjacentHit(
  hits: ImageSearchHit[],
  id: string | null,
  step: number
): ImageSearchHit | undefined {
  const index = hits.findIndex((hit) => hit.id === id)

  if (index === -1) {
    return undefined
  }

  return hits[index + step]
}

/** Returns the last hit if it's on a later row than `id`, e.g. down onto a shorter last row */
export function getLastHitBelow(
  hits: ImageSearchHit[],
  id: string | null,
  columns: number
): ImageSearchHit | undefined {
  const index = hits.findIndex((hit) => hit.id === id)
  const lastIndex = hits.length - 1

  if (index === -1 || Math.floor(lastIndex / columns) <= Math.floor(index / columns)) {
    return undefined
  }

  return hits[lastIndex]
}

/** Reads the column count of a CSS grid, as it's set by container queries, not by JS */
export function countGridColumns(grid: HTMLElement | null): number {
  if (!grid) {
    return 1
  }

  const tracks = getComputedStyle(grid).gridTemplateColumns
    .split(' ')
    .filter((track) => track !== '' && track !== 'none')

  return Math.max(tracks.length, 1)
}
